import React from "react";
import { connect, ConnectedProps } from "react-redux";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import dayjs from "dayjs";
import Common from "us.common";
import { CaseDelayAction } from "us.collection.case/actions";
import { caseDocuments } from "us.collection.documents/actions";
import { RootState } from "us.helper/types";
import { DelayPaymentSchema } from "../../Validations";
import { AddDelayPayment } from "us.collection.case/repository";

const { $Form, $Button, $DatePicker, $Popconfirm, $Row, $Col, $TextArea } =
  Common.Components;

/**
 * @description - Add delay payment form component
 * @link Design Document -
 * @author Tharanga Niroshana <tharangan@unicorn-solutions.com>
 * @since 23/09/2025
 */

type DelayPaymentProps = {
  onClose: () => void;
};

const DelayPayment: React.FC<DelayPaymentProps & PropsFromRedux> = (props) => {
  const { t } = useTranslation();

  const { onClose, currentDateFormat, metaData, saveDelay } = props;

  const { caseId, entityType } = metaData.data ?? {};

  const handleSubmit = (values: any) => {
    const request = AddDelayPayment.call(values, caseId, entityType);
    saveDelay && saveDelay(request);
    onClose();
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{
        delayUntil: "",
        coment: "",
      }}
      validationSchema={DelayPaymentSchema}
      onSubmit={handleSubmit}
    >
      {({
        values,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        isValidating,
        isValid,
        resetForm,
        ...restProps
      }: any) => (
        <$Form>
          <div>
            <$Row gutter={16} className="mb-3">
              <$Col span={7}>
                <div className="flex-fill" data-testid="date-of-payment-label">
                  <$DatePicker
                    formitem
                    required
                    allowClear
                    data-testid="date-of-payment"
                    label={t("US.COLLECTION.CASE:CASE_DELAY.DELAY_UNTIL")}
                    name="delayUntil"
                    size="small"
                    style={{
                      width: "100%",
                    }}
                    format={currentDateFormat}
                    defaultValue={values.delayUntil}
                    value={values.delayUntil}
                    disabledDate={(day: any) =>
                      day.isBefore(dayjs().endOf("day"))
                    }
                  />
                </div>
              </$Col>
            </$Row>
            <$Row gutter={16} className="mb-4">
              <$Col span={24} data-testid="case-note">
                <$TextArea
                  name="comment"
                  autoSize={{
                    minRows: 2,
                    maxRows: 6,
                  }}
                  label={t("US.COLLECTION.CASE:CASE_DELAY.NOTE")}
                />
              </$Col>
            </$Row>

            <div className="drawer-footer-fixed align-content-center justify-content-end">
              <div>
                <$Button
                  className="mr-2"
                  onClick={() => handleSubmit()}
                  type="primary"
                  htmlType="submit"
                  data-testid="save-btn"
                >
                  {t("US.COLLECTION.COMMON:COMMON.SAVE")}
                </$Button>
                <$Popconfirm
                  title={t("US.COLLECTION.CASE:CASE_DELAY.CANCEL_CONFORM")}
                  // "Your existing data will be lost. Do you wish to continue?"
                  placement="topLeft"
                  onConfirm={() => onClose()}
                  okText={t("US.COLLECTION.COMMON:COMMON.YES")}
                  cancelText={t("US.COLLECTION.COMMON:COMMON.NO")}
                >
                  <$Button>{t("US.COLLECTION.COMMON:COMMON.CANCEL")}</$Button>
                </$Popconfirm>
              </div>
            </div>
          </div>
        </$Form>
      )}
    </Formik>
  );
};

const mapStateToProps = (state: RootState) => {
  const { common, domainView } = state;
  const { currentDateFormat } = common;
  const { metaData } = domainView;
  return {
    currentDateFormat,
    metaData,
  };
};

const { delay } = CaseDelayAction;

const mapDispatchToProps = {
  saveDelay: delay.save,
};
const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DelayPayment);
